import React, { useState } from "react";
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfYear } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar, Filter, X, Search, ChevronDown } from "lucide-react";

const periodosPredefinidos = [
  { label: "Hoje", value: "hoje", getDates: () => ({ inicio: new Date(), fim: new Date() }) },
  { label: "Últimos 7 dias", value: "7dias", getDates: () => ({ inicio: subDays(new Date(), 7), fim: new Date() }) },
  { label: "Últimos 30 dias", value: "30dias", getDates: () => ({ inicio: subDays(new Date(), 30), fim: new Date() }) },
  { label: "Este mês", value: "mes", getDates: () => ({ inicio: startOfMonth(new Date()), fim: endOfMonth(new Date()) }) },
  { label: "Mês passado", value: "mes_passado", getDates: () => ({ inicio: startOfMonth(subMonths(new Date(), 1)), fim: endOfMonth(subMonths(new Date(), 1)) }) },
  { label: "Últimos 3 meses", value: "3meses", getDates: () => ({ inicio: subMonths(new Date(), 3), fim: new Date() }) },
  { label: "Últimos 6 meses", value: "6meses", getDates: () => ({ inicio: subMonths(new Date(), 6), fim: new Date() }) },
  { label: "Este ano", value: "ano", getDates: () => ({ inicio: startOfYear(new Date()), fim: new Date() }) },
  { label: "Personalizado", value: "custom", getDates: () => null }
];

export default function FiltrosAvancados({ 
  filtros, 
  onFiltrosChange, 
  categorias = [], 
  tipos = [],
  statusOptions = [],
  showValorRange = true
}) {
  const [open, setOpen] = useState(false);
  const [periodoSelecionado, setPeriodoSelecionado] = useState("30dias");

  const handlePeriodoChange = (value) => {
    setPeriodoSelecionado(value);
    const periodo = periodosPredefinidos.find(p => p.value === value);
    if (periodo && value !== 'custom') {
      const dates = periodo.getDates();
      onFiltrosChange({
        ...filtros,
        dataInicio: format(dates.inicio, 'yyyy-MM-dd'),
        dataFim: format(dates.fim, 'yyyy-MM-dd')
      });
    }
  };

  const handleChange = (campo, valor) => {
    onFiltrosChange({ ...filtros, [campo]: valor });
  };

  const limparFiltros = () => {
    onFiltrosChange({
      busca: "",
      dataInicio: "",
      dataFim: "",
      categoria: "todas",
      tipo: "todos",
      status: "todos",
      valorMin: "",
      valorMax: ""
    });
    setPeriodoSelecionado("");
  };

  const filtrosAtivos = Object.entries(filtros).filter(([key, value]) => {
    if (key === 'busca' && value) return true;
    if (key === 'dataInicio' && value) return true;
    if (key === 'dataFim' && value) return true;
    if (key === 'categoria' && value && value !== 'todas') return true;
    if (key === 'tipo' && value && value !== 'todos') return true;
    if (key === 'status' && value && value !== 'todos') return true;
    if (key === 'valorMin' && value) return true;
    if (key === 'valorMax' && value) return true;
    return false;
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-center">
        {/* Busca */}
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Buscar por descrição, categoria, fornecedor..."
            value={filtros.busca || ""}
            onChange={(e) => handleChange('busca', e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Período Rápido */}
        <Select value={periodoSelecionado} onValueChange={handlePeriodoChange}>
          <SelectTrigger className="w-44">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            {periodosPredefinidos.map(p => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Filtros Avançados */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="w-4 h-4" />
              Filtros
              {filtrosAtivos.length > 0 && (
                <Badge className="bg-indigo-600 text-white ml-1">{filtrosAtivos.length}</Badge>
              )}
              <ChevronDown className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4" align="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">Filtros Avançados</h4>
                <Button variant="ghost" size="sm" onClick={limparFiltros} className="text-red-500">
                  <X className="w-4 h-4 mr-1" /> Limpar
                </Button>
              </div>

              {/* Período Personalizado */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Data Início</Label>
                  <Input
                    type="date"
                    value={filtros.dataInicio || ""}
                    onChange={(e) => {
                      handleChange('dataInicio', e.target.value);
                      setPeriodoSelecionado('custom');
                    }}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Data Fim</Label>
                  <Input
                    type="date"
                    value={filtros.dataFim || ""}
                    onChange={(e) => {
                      handleChange('dataFim', e.target.value);
                      setPeriodoSelecionado('custom');
                    }}
                    className="h-9"
                  />
                </div>
              </div>

              {/* Tipo */}
              {tipos.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">Tipo</Label>
                  <Select value={filtros.tipo || "todos"} onValueChange={(v) => handleChange('tipo', v)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {tipos.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Categoria */}
              {categorias.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">Categoria</Label>
                  <Select value={filtros.categoria || "todas"} onValueChange={(v) => handleChange('categoria', v)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todas">Todas</SelectItem>
                      {categorias.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Status */}
              {statusOptions.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">Status</Label>
                  <Select value={filtros.status || "todos"} onValueChange={(v) => handleChange('status', v)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {statusOptions.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Faixa de Valor */}
              {showValorRange && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Valor Mínimo</Label>
                    <Input
                      type="number"
                      placeholder="R$ 0"
                      value={filtros.valorMin || ""}
                      onChange={(e) => handleChange('valorMin', e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Valor Máximo</Label>
                    <Input
                      type="number"
                      placeholder="R$ ∞"
                      value={filtros.valorMax || ""}
                      onChange={(e) => handleChange('valorMax', e.target.value)}
                      className="h-9"
                    />
                  </div>
                </div>
              )}

              <Button onClick={() => setOpen(false)} className="w-full bg-indigo-600 hover:bg-indigo-700">
                Aplicar Filtros
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Filtros Ativos */}
      {filtrosAtivos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {filtros.dataInicio && filtros.dataFim && (
            <Badge variant="secondary" className="gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(filtros.dataInicio).toLocaleDateString('pt-BR')} - {new Date(filtros.dataFim).toLocaleDateString('pt-BR')}
              <X className="w-3 h-3 cursor-pointer ml-1" onClick={() => onFiltrosChange({...filtros, dataInicio: "", dataFim: ""})} />
            </Badge>
          )}
          {filtros.categoria && filtros.categoria !== 'todas' && (
            <Badge variant="secondary" className="gap-1">
              {filtros.categoria}
              <X className="w-3 h-3 cursor-pointer ml-1" onClick={() => handleChange('categoria', 'todas')} />
            </Badge>
          )}
          {filtros.tipo && filtros.tipo !== 'todos' && (
            <Badge variant="secondary" className="gap-1">
              {filtros.tipo === 'receita' ? 'Receitas' : 'Despesas'}
              <X className="w-3 h-3 cursor-pointer ml-1" onClick={() => handleChange('tipo', 'todos')} />
            </Badge>
          )}
          {(filtros.valorMin || filtros.valorMax) && (
            <Badge variant="secondary" className="gap-1">
              R$ {filtros.valorMin || '0'} - R$ {filtros.valorMax || '∞'}
              <X className="w-3 h-3 cursor-pointer ml-1" onClick={() => onFiltrosChange({...filtros, valorMin: "", valorMax: ""})} />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}